import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  ApplicationRef,
  ChangeDetectorRef,
  ComponentFactoryResolver,
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  Renderer2,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { KzPopupService } from '../popup/kz-popup.service';
import { KzTutorialService } from './kz-tutorial.service';
import { KzTutorialComponent } from './kz-tutorial/kz-tutorial.component';
import { KzPopup } from '../popup/kz-popup';
import { placeElement, Position, SimplePosition } from '../../utils/positioning';
import { take, tap } from 'rxjs/operators';
import { merge } from 'rxjs';
import { MediaQueriesService } from '../../../shared/services/utilities/media-queries.service';
import { Subscription } from 'rxjs/internal/Subscription';

@Directive({
  selector: '[kzTutorial]',
  exportAs: 'kzTutorial',
})
export class KzTutorialDirective implements OnInit, OnDestroy {
  private _popup: KzPopup<KzTutorialComponent>;

  private _KzTutorialStep: number;
  private _KzTutorialHelperStepContent: string | TemplateRef<any>;
  private _KzTutorialHelperStepTitle: string;
  private _KzTutorialHelperStepPosition: Position = 'right-bottomArrow';
  private _KzTutorial;
  private _subscription: Subscription;

  @Input()
  set tutorialStep(num: number) {
    this._KzTutorialStep = num;
  }
  get tutorialStep(): number {
    return this._KzTutorialStep;
  }

  @Input()
  set tutorialContent(content: string | TemplateRef<any>) {
    this._KzTutorialHelperStepContent = content;
  }
  get tutorialContent(): string | TemplateRef<any> {
    return this._KzTutorialHelperStepContent;
  }

  @Input()
  set tutorialTitle(title: string) {
    this._KzTutorialHelperStepTitle = title;
  }
  get tutorialTitle(): string {
    return this._KzTutorialHelperStepTitle;
  }

  @Input()
  set tutorialPosition(pos: SimplePosition) {
    this._KzTutorialHelperStepPosition = (pos === 'center') ? undefined : <Position>`${pos}-bottomArrow`;
  }

  getKzTutorialHelperStepPosition(): Position {
    return this._KzTutorialHelperStepPosition;
  }

  // Specify Tutorial context if many tutorials on same page
  @Input()
  set kzTutorial(tutorial: string) {
    tutorial = tutorial || 'default';
    this._KzTutorial = tutorial;
  }

  get kzTutorial(): string {
    return this._KzTutorial;
  }

  @Output()
  readonly opened: EventEmitter<void> = new EventEmitter<void>();
  @Output()
  readonly closed: EventEmitter<void> = new EventEmitter<void>();

  constructor(
    private readonly kzTutorialService: KzTutorialService,
    private readonly _viewContainerRef: ViewContainerRef,
    private readonly _componentFactoryResolver: ComponentFactoryResolver,
    private readonly _injector: Injector,
    private readonly _applicationRef: ApplicationRef,
    private readonly _renderer: Renderer2,
    private readonly _elementRef: ElementRef,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _mediaQueriesService: MediaQueriesService,
    @Inject(DOCUMENT) private _document: Document,
    @Inject(PLATFORM_ID) private platformId: object,
    private _kzPopupService: KzPopupService,
  ) {
    if (isPlatformBrowser(platformId) && window.innerWidth > 799 ) {
      this._popup = this._kzPopupService.create(KzTutorialComponent);
    }
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId) && window.innerWidth > 799) {
      this.kzTutorialService.registerItem(this.kzTutorial, this);
    }
  }

  ngOnDestroy(): void {
    if (isPlatformBrowser(this.platformId)) {
      if (this._popup) {
        this._popup.close();
      }
      this.kzTutorialService.unregisterItem(this.kzTutorial, this);
    }
    if (this._subscription && !this._subscription.closed) {
      this._subscription.unsubscribe();
    }
  }

  open(itemOrder: number, totalItems: number) {
    this._popup.open(typeof this.tutorialContent !== 'string' ? this.tutorialContent : ' ' );
    this._popup.componentRef.instance.tutorialPosition = this.getKzTutorialHelperStepPosition();
    this._popup.componentRef.instance.itemOrder = itemOrder;
    this._popup.componentRef.instance.totalItems = totalItems;
    this._popup.componentRef.instance.title = this.tutorialTitle;
    this._popup.componentRef.instance.description = typeof this.tutorialContent === 'string' ? this.tutorialContent : undefined;
    this._subscription = merge(
      this._popup.componentRef.instance.closed.pipe(
        take(1),
        tap(() => this.close())
      ),
      this._popup.componentRef.instance.next.pipe(
        take(1),
        tap(() => this.close()),
        tap(() => this.kzTutorialService.nextItem()),
      ),
      this._popup.componentRef.instance.previous.pipe(
        take(1),
        tap(() => this.close()),
        tap(() => this.kzTutorialService.previousItem()),
      ),
      this._popup.componentRef.instance.hide.pipe(
        take(1),
        tap(() => this.kzTutorialService.hideTutorial(this.kzTutorial)),
        tap(() => this.close())
      )
    ).subscribe();
    this._document.querySelector('body').appendChild(this._popup.componentRef.location.nativeElement);
    this._changeDetectorRef.detectChanges();
    if (this.getKzTutorialHelperStepPosition()) {
      this._placeTutorialElement();
    }

    if (this._popup.opened) {
      this.opened.emit();
    }
  }

  close() {
    this._popup.close();
    this._document.body.style.overflow = 'auto';
    if (this._popup.closed) {
      this.closed.emit();
    }
  }


  @HostListener('window:resize')
  onWindowResize(event) {
    this._placeTutorialElement();
  }

  private _placeTutorialElement() {
    if (this._popup?.componentRef) {
      // place popup only if getKzTutorialHelperStepPosition specified, else it is a window centered popin
      !!this.getKzTutorialHelperStepPosition() && placeElement(
        this._elementRef.nativeElement,
        this._popup.componentRef.instance.kzHelperPopin.nativeElement,
        this.getKzTutorialHelperStepPosition(),
        this._popup.componentRef.instance.kzHelperOverlay.nativeElement,
        true,
        true
      );
      this._changeDetectorRef.detectChanges();
      this._changeDetectorRef.markForCheck();
    }
  }
}
